import { Deserializer } from "jsonapi-serializer";
import { DEF_PG_SIZE_EXTRA_LRG } from '../constants/maximum-page-size';
import { TranslationKeys } from "../contexts/TranslationKeys";
import { Translator } from "../contexts/translator";
import { EventType, EventTypeWrapper } from "../models/EventType";
import { genericError } from "../models/GenericError";
import { ListQuery } from "../models/ListQuery";
import { Name } from "../models/Name";
import { PaginationModel } from "../models/Pagination";
import { StoreEntityResult } from "../models/services/StoreEntityResult";
import { APIConnector } from "./api/APIConnector";
import { createPagination } from "./utilities/paginationBuilder";
import { getConstraintViolation, processServerError } from "./utilities/ServiceError";
import { createPartialURL, getFilterClause } from "./utilities/urlBuilder";
import { INTERNAL_SERVER_ERROR, NO_CONTENT } from "../constants/response-status-codes";

const apiConnector = new APIConnector();

export const getEventTypeNamesWithPagination = async (listQuery?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        const paginationString = createPartialURL(listQuery?.goToPage, listQuery?.pageSize ?? DEF_PG_SIZE_EXTRA_LRG, listQuery?.sortBy, filters);
        const url = `eventType${paginationString ? `?${paginationString.slice(1)}` : ''}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, apiEventTypes: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const eventTypes: Name[] = apiEventTypes.map((eventType) => {
                    return {
                        value: eventType['id'],
                        label: eventType['trn-name']
                    };
                });

                resolve({ rows: eventTypes, pagination: newPagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getEventTypeNames = async (): Promise<Name[]> => {
    try {
        const url = `eventType?fields[eventType]=trnName`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, data: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const profiles: Name[] = data.map((profile): Name => {
                    return {
                        value: profile['id'],
                        label: profile['trn-name']
                    };
                });

                return resolve(profiles);
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getEventTypes = async (translate: Translator, listQuery?: ListQuery): Promise<EventTypeWrapper> => {
    try {
        const filters: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        const paginationString = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filters);
        const url = `eventType?fields[eventType]=etAlarm,etPriorityLevel,trnName${paginationString}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, apiEventTypes: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const eventTypes: EventType[] = apiEventTypes.map((eventType) => {
                    return {
                        id: eventType['id'],
                        name: eventType['trn-name'],
                        etAlarmLabel: eventType['et-alarm'] === 0 ? translate.byKey(TranslationKeys.no) : translate.byKey(TranslationKeys.yes),
                        etAlarm: eventType['et-alarm'].toString(),
                        etPriorityLevel: eventType['et-priority-level'].toString(),
                        etPriorityLevelLabel: eventType['et-priority-level'] === 0 ?  translate.byKey(TranslationKeys.standard) : translate.byKey(TranslationKeys.high),
                        nameOriginal: eventType['trn-name'],
                    };
                });

                resolve({ rows: eventTypes, pagination: newPagination });
            });
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const storeEventType = async (eventType: EventType): Promise<StoreEntityResult> => {
    try {
        const record = {
            data: {
                type: "eventType",
                id: eventType.id,
                attributes: {
                    etPriorityLevel: eventType.etPriorityLevel,
                    etAlarm: eventType.etAlarm,
                }
            }
        }

        const url = `eventType/${eventType.id}`;
        let response: Response;

        await apiConnector.sendData(url, false, record).then((data) => {
            response = data;
        }).catch((error) => {
            return Promise.reject(processServerError(error));
        })

        return new Promise(async (resolve, reject) => {
            if (response) {
                if (response.status === NO_CONTENT) {
                    return resolve({ id: record.data.id, success: true })
                }

                const content = await response.json();
                return reject({ status: response.status, id: record.data.id, success: false, message: content.errors.map(error => getConstraintViolation(error.detail)) });
            }

            return reject({ status: INTERNAL_SERVER_ERROR, id: record.data.id, success: false, message: genericError });
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const getEventTypeById = async (id: string, translate: Translator): Promise<EventType> => {
    try {
        const url = `eventType/${id}?fields[eventType]=etAlarm,etPriorityLevel,trnName`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, data: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const eventType: EventType = {
                    id: data['id'],
                    name: data['trn-name'],
                    etAlarmLabel: data['et-alarm'] === 0 ? translate.byKey(TranslationKeys.no) : translate.byKey(TranslationKeys.yes),
                    etAlarm: data['et-alarm'].toString(),
                    etPriorityLevel: data['et-priority-level'].toString(),
                    etPriorityLevelLabel: data['et-priority-level'] === 0 ? translate.byKey(TranslationKeys.standard) : translate.byKey(TranslationKeys.high),
                    nameOriginal: data['trn-name'],
                };

                resolve(eventType);
            });
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}